import React, { useState, useEffect } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Link,
} from "@mui/material";
import api from "../../api";
import safeLogger from "../../utils/safeLogger";
import { useNavigate, useLocation } from "react-router-dom";
import { isTokenValid } from "../../utils/tokenUtils"; // Importação corrigida
import { useHistoryContext } from "../../context/HistoryContext";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const { setHistory } = useHistoryContext();

  const navigate = useNavigate();
  const location = useLocation();
  let message = null;
  if (location.state?.from) message = "Sua sessão expirou. Por favor, faça login novamente.";
  if (location.state?.expired)
    message = "Sua assinatura expirou. Por favor, renove para continuar utilizando o sistema.";

  // Se o ProtectedRoute ou o backend tiver passado o e-mail no state, persista-o para uso na renovação
  useEffect(() => {
    const stateEmail = location.state?.email;
    if (location.state?.expired && stateEmail && validateEmail(stateEmail)) {
      localStorage.setItem("email", stateEmail);
    }
  }, [location.state]);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePassword = (password) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/.test(
      password
    );

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    if (!validateEmail(value)) {
      setErrors((prev) => ({ ...prev, email: "E-mail inválido." }));
    } else {
      setErrors((prev) => {
        const { email, ...rest } = prev;
        return rest;
      });
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);

    if (!validatePassword(value)) {
      setErrors((prev) => ({
        ...prev,
        password:
          "A senha deve ter ao menos 6 caracteres, incluindo letra maiúscula, minúscula, número e caractere especial.",
      }));
    } else {
      setErrors((prev) => {
        const { password, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleFormChange = () => {
    if (validateEmail(email) && validatePassword(password)) {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  };

  const handleLogin = async () => {
    try {
      // Faz a requisição para o endpoint de login
      const response = await api.post("/auth/login", { email, password });

      const token = response.data.token;

      // Valida o token antes de continuar
      if (!isTokenValid(token)) {
        throw new Error("Token recebido é inválido ou expirado.");
      }

      // Verifica se há um redirecionamento necessário devido ao status
      if (response.data.redirect) {
        localStorage.setItem("email", email); // Salva o e-mail para reutilização no redirecionamento
        setErrors({
          form: response.data.message || "Conta expirada ou inativa.",
        });
        setTimeout(() => {
          navigate(response.data.redirect); // Redireciona para o fluxo de renovação
        }, 3000);
        return;
      }

      // Salvar o token e perfil no localStorage
      localStorage.setItem("token", token);
      localStorage.setItem("profile", response.data.profile);
      localStorage.setItem("userId", response.data.userId); // Armazena o ID do usuário
      localStorage.setItem("email", response.data.email);

      // Persistir o histórico no localStorage
      const historyResponse = await api.get("/api/history", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const fetchedHistory = historyResponse.data;
      localStorage.setItem("history", JSON.stringify(fetchedHistory));

      // Atualiza o estado do histórico
      setHistory(fetchedHistory);

      // Configurar o cabeçalho Authorization globalmente
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      // Define uma mensagem de sucesso
      setSuccessMessage("Login realizado com sucesso!");
      setErrors({});
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);

      // Verifica se o login é bem-sucedido
      if (response.status === 200) {
        localStorage.setItem("token", response.data.token); // Armazena o token no localStorage
        localStorage.setItem("email", email); // Armazena o email no localStorage
        //window.location.href = "/dashboard"; // Redireciona para a dashboard ou rota protegida
      }

      // Redirecionamento com base no perfil do usuário
      switch (response.data.profile) {
        case "Administrador":
          navigate("/admin-dashboard");
          break;
        case "Gestor":
          navigate("/manager-tools");
          break;
        case "Dev":
          navigate("/code-analysis");
          break;
        default:
          navigate("/code-analysis");
          break;
      }
    } catch (error) {
      // Verifica se o erro é relacionado ao status expirado (403)
      if (
        error.response?.status === 403 &&
        error.response?.data?.message.includes("expirou")
      ) {
        setErrors({
          form: error.response.data.message || "Conta expirada ou inativa.",
        });
        // Salva o e-mail para uso no fluxo de renovação antes de redirecionar
        if (validateEmail(email)) {
          localStorage.setItem("email", email);
        }
        setTimeout(() => {
          navigate("/renew", { state: { email } }); // Redireciona para a página de renovação
        }, 3000);
        return;
      }

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Erro ao fazer login.";
      // Salvar o e-mail no localStorage para renovação
      if (error.response?.data?.redirect) {
        localStorage.setItem("email", email); // Salva o e-mail ao redirecionar
        setErrors({ form: errorMessage });
        setTimeout(() => {
          navigate(error.response.data.redirect); // Redireciona após 3 segundos
        }, 3000);
        return;
      }

      safeLogger.error("Erro no login:", error.message);
      setErrors({ form: errorMessage });
      setTimeout(() => {
        setErrors({}); // Limpa a mensagem de erro após 3 segundos
      }, 3000);
      setSuccessMessage("");
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, textAlign: "center" }}>
      <Typography variant="h4" gutterBottom>
        Login
      </Typography>

      {message && (
        <Typography sx={{ color: "red", fontWeight: "bold", mb: 2 }}>
          {message}
        </Typography>
      )}

      <Box sx={{ mt: 2 }}>
        <TextField
          label="E-mail"
          variant="outlined"
          fullWidth
          value={email}
          onChange={(e) => {
            handleEmailChange(e);
            handleFormChange();
          }}
          error={!!errors.email}
          helperText={
            errors.email && (
              <span style={{ color: "red", fontWeight: "bold" }}>
                {errors.email}
              </span>
            )
          }
          sx={{ mb: 2 }}
        />

        <TextField
          label="Senha"
          variant="outlined"
          type="password"
          fullWidth
          value={password}
          onChange={(e) => {
            handlePasswordChange(e);
            handleFormChange();
          }}
          error={!!errors.password}
          helperText={
            errors.password && (
              <span style={{ color: "red", fontWeight: "bold" }}>
                {errors.password}
              </span>
            )
          }
          sx={{ mb: 2 }}
        />

        {errors.form && (
          <Typography sx={{ color: "red", fontWeight: "bold", mt: 2 }}>
            {errors.form}
          </Typography>
        )}

        <Button
          variant="contained"
          color="primary"
          disabled={isButtonDisabled}
          fullWidth
          sx={{ mt: 2 }}
          onClick={handleLogin}
        >
          Entrar
        </Button>

        {successMessage && (
          <Typography sx={{ color: "green", fontWeight: "bold", mt: 2 }}>
            {successMessage}
          </Typography>
        )}

        <Box sx={{ mt: 2 }}>
          <Link href="/recover-password" underline="hover">
            Esqueci minha senha
          </Link>
        </Box>
      </Box>
    </Container>
  );
};

export default Login;
